/**
 * The light/dark segmented control in the header (SPEC §9.2).
 *
 * Two segments, the active one carrying `--accent` — the same wayfinding colour
 * the rail stripe and KPI hairline use, so the toggle reads as part of the same
 * system. Presentation only: it writes the choice through `setTheme`, which
 * owns the `data-theme` attribute and the persistence.
 */

import { Moon, Sun } from 'lucide-react'

import { useTheme, type Theme } from '../lib/theme'

const OPTIONS: readonly { value: Theme; label: string; Icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
]

export function ThemeToggle() {
  const { theme, setTheme } = useTheme()

  return (
    <div
      role="group"
      aria-label="Theme"
      className="inline-flex items-center gap-0.5 rounded-[var(--radius-md)] border border-[var(--color-rule)] p-0.5"
    >
      {OPTIONS.map(({ value, label, Icon }) => {
        const active = theme === value
        return (
          <button
            key={value}
            type="button"
            aria-pressed={active}
            title={`${label} theme`}
            onClick={() => {
              setTheme(value)
            }}
            className={`inline-flex items-center gap-1 rounded-[var(--radius-sm)] px-2 py-1 text-[length:var(--text-2xs)] font-medium ${
              active
                ? 'bg-[var(--accent)] text-[var(--color-surface-raised)]'
                : 'text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]'
            }`}
          >
            <Icon aria-hidden className="size-3.5" />
            {label}
          </button>
        )
      })}
    </div>
  )
}
